import { AUDIO } from '../config/tuning';

/**
 * The Web Audio graph, its buses, and the autoplay policy (SPEC §3.11, §5.4).
 *
 * Three buses, mixable independently, because SPEC is explicit that "RWR must remain audible when
 * effects are turned down". A single master with per-source gains cannot express that: the RWR has
 * to hang off its own trunk so a player who mutes explosions still hears a launch warning. That is
 * a safety property of the interface, not a mixing preference.
 *
 * **Autoplay.** Browsers refuse to start an `AudioContext` without a user gesture, and a suspended
 * context silently swallows everything scheduled on it. So the context is created lazily on the
 * first gesture, every call site is safe against `null`, and nothing throws if audio never starts
 * at all — a game that crashes because the player has not clicked yet is worse than a silent one.
 *
 * Frequencies in hertz, times in seconds, gains unitless 0–1.
 */

export const AudioBus = {
  Effects: 'effects',
  Rwr: 'rwr',
} as const;
export type AudioBusId = (typeof AudioBus)[keyof typeof AudioBus];

export interface BusLevels {
  master: number;
  effects: number;
  rwr: number;
}

export class AudioEngine {
  /** Null until a user gesture has unlocked the context. */
  private context: AudioContext | null = null;

  private master: GainNode | null = null;
  private effects: GainNode | null = null;
  private rwr: GainNode | null = null;

  /** Shared white-noise buffer. Generated once; every noise source reads from it. */
  private noiseBuffer: AudioBuffer | null = null;

  private readonly levels: BusLevels = {
    master: AUDIO.masterGain,
    effects: AUDIO.effectsGain,
    rwr: AUDIO.rwrGain,
  };

  private startedValue = false;

  get started(): boolean {
    return this.startedValue;
  }

  /** True when audio can actually be heard right now. */
  get running(): boolean {
    return this.context !== null && this.context.state === 'running';
  }

  get now(): number {
    return this.context?.currentTime ?? 0;
  }

  get ctx(): AudioContext | null {
    return this.context;
  }

  /**
   * Create the context. Must be called from a user gesture handler.
   *
   * Idempotent, and safe to call from every plausible gesture — the cheapest way to satisfy the
   * autoplay policy is to try on all of them rather than guess which one the browser will accept.
   */
  unlock(): void {
    if (this.context === null) {
      // `webkitAudioContext` is a genuine external boundary: older Safari exposes only the
      // prefixed constructor, and there is no typed surface for it (CLAUDE.md `any` exemption).
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor === undefined) return;
      try {
        this.context = new Ctor();
      } catch {
        // A browser that refuses to construct a context leaves the game silent, not broken.
        return;
      }
      this.build();
      this.startedValue = true;
    }
    if (this.context.state === 'suspended') void this.context.resume();
  }

  private build(): void {
    const context = this.context;
    if (context === null) return;

    this.master = context.createGain();
    this.master.gain.value = this.levels.master;
    this.master.connect(context.destination);

    this.effects = context.createGain();
    this.effects.gain.value = this.levels.effects;
    this.effects.connect(this.master);

    this.rwr = context.createGain();
    this.rwr.gain.value = this.levels.rwr;
    this.rwr.connect(this.master);

    this.noiseBuffer = createNoiseBuffer(context);
  }

  bus(id: AudioBusId): GainNode | null {
    return id === AudioBus.Rwr ? this.rwr : this.effects;
  }

  /** Set a bus level, 0–1. Persisted by the settings screen at M10. */
  setLevel(bus: keyof BusLevels, value: number): void {
    const clamped = value < 0 ? 0 : value > 1 ? 1 : value;
    this.levels[bus] = clamped;
    const node = bus === 'master' ? this.master : bus === 'effects' ? this.effects : this.rwr;
    if (node !== null && this.context !== null) {
      node.gain.setTargetAtTime(clamped, this.context.currentTime, 0.02);
    }
  }

  getLevels(): Readonly<BusLevels> {
    return this.levels;
  }

  /** A source node reading the shared noise buffer, looping. Caller starts and stops it. */
  createNoiseSource(): AudioBufferSourceNode | null {
    if (this.context === null || this.noiseBuffer === null) return null;
    const source = this.context.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    return source;
  }

  suspend(): void {
    if (this.context !== null && this.context.state === 'running') void this.context.suspend();
  }

  resume(): void {
    if (this.context !== null && this.context.state === 'suspended') void this.context.resume();
  }

  dispose(): void {
    if (this.context === null) return;
    void this.context.close();
    this.context = null;
    this.master = null;
    this.effects = null;
    this.rwr = null;
    this.noiseBuffer = null;
    this.startedValue = false;
  }
}

/**
 * Two seconds of white noise, looped by everything that needs a noise floor.
 *
 * One buffer rather than one per source: a `BufferSource` is cheap but a megabyte of samples is
 * not, and every noise consumer here (airflow, buffet, launch, detonation, impact) wants the same
 * spectrum shaped differently by a filter. Deliberately *not* seeded from the game PRNG — this is
 * presentation, never simulation, so it cannot affect determinism (CLAUDE.md).
 */
function createNoiseBuffer(context: AudioContext): AudioBuffer {
  const seconds = 2;
  const length = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  // A small xorshift so the noise is identical run to run, which makes A/B tuning possible.
  let state = 0x1234abcd;
  for (let i = 0; i < length; i += 1) {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    data[i] = ((state >>> 0) / 4294967296) * 2 - 1;
  }
  return buffer;
}
